import { api } from 'api'
import { path } from 'ramda'
import { TimelineShift } from 'api/shift'

interface GetCompanyShiftDetailResponse extends TimelineShift {
  shiftEndWorkApprovalDelayHours: number
}

export async function getCompanyShiftDetail({ ids }: { ids: number[] }) {
  const data = await api.post(
    '/company/graphql_dispatch/shift_detail_17608ea0',
    {
      ids,
      includeEstimate: true,
    }
  )

  const shifts = path(
    ['data', 'data', 'company', 'shiftsDetail'],
    data
  ) as GetCompanyShiftDetailResponse[]
  return shifts.map((shift) => ({
    ...shift,
    id: Number(shift.id),
  }))
}

type GetShiftDetailResponse = {
  id: number
  companyId: number
  startsAt: string
  endsAt: string
  shiftType: string
  isTryout: boolean
  workSource: 'workwhile' | 'dayforce'
  workersNeeded: number
  numWorkersPending: number
  payRate: string | null
  payLumpSum: string | null
  chargeRate: string | null
  chargeLumpSum: string | null
  billRateId: number | null
  billRate: string | null
  lunchLengthInMinutes: number
  supervisor: string | null
  supervisorPhone: string | null
  clockInCode: number | null
  clockOutCode: number | null
  requiresClockInCode: boolean
  requiresClockOutCode: boolean
  requiresQrClockInCode: boolean
  requiresQrClockOutCode: boolean
  locationless: boolean
  shiftEndPaymentDelayHours: number
  shiftEndWorkApprovalDelayHours: number
  rostersHoldExpiresAt: string | null
  expectedMileage: number | null
  hasAcknowledgedOddbRequirements: boolean
  bundleMetadata: { bundleIndex: number; bundleSize: number }
  recommendBackupShift: boolean
  shiftToBackup: { startsAt: string } | null
  position: {
    id: number
    name: string
    hidden: boolean
    roster: {
      id: number
      name: string
      rosterUsers: { userId: number; status: string }[]
    } | null
  }
  location: {
    id: number
    name: string
    address: {
      street: string | null
      unit: string | null
      city: string | null
      state: string | null
      zip: string | null
      lat: number | null
      long: number | null
      timezone: string | null
    }
  } | null
  rosters: {
    id: number
    name: string
    isSpecificWorkerRequest: boolean
    workers: { id: number; name: string | null; profilePicUrl: string | null }[]
  }[]
  supervisors: {
    id: number
    name: string
    phoneNumber: string | null
    userId: number | null
    status: string
  }[]
  shiftBonuses: { amount: string }[]
  assignment: { id: number; name: string } | null
  cancellationPolicy: {
    policyWindows: {
      cutoffTimeHours: number
      chargePercentage: number | null
      chargeMinutes: number | null
    }[]
  }
  cushionChargePolicy: { chargeAfterMinutes: number } | null
  minimumPayPolicy: { minPayLengthInMinutes: number } | null
  totalEstimatedCharge: string
  leadShifts: { id: number; workersNeeded: number }[]
}

/**
 * Fetches a shift's detail over REST and returns it in the same shape as
 * `getCompanyShiftDetail`. Work is fetched separately by `getShiftWork`.
 */
export async function getCompanyShiftDetailRest(
  { companyId, shiftId }: { companyId: number; shiftId: number },
  signal?: AbortSignal
) {
  const response = await api.get<GetShiftDetailResponse>(
    `/company/${companyId}/shifts/${shiftId}`,
    { signal }
  )
  const { lunchLengthInMinutes, minimumPayPolicy, ...detail } = response.data

  const shift = {
    ...detail,
    lunchLength: lunchLengthInMinutes,
    minimumPayPolicy: minimumPayPolicy
      ? { minPayLength: minimumPayPolicy.minPayLengthInMinutes }
      : null,
  } as unknown as GetCompanyShiftDetailResponse

  return shift
}
